/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { BEM, useTranslate } from "@adapptio/lib-components";
import { AssetEdit } from "../../AssetEdit";
import { ToolbarButton } from "../../../../../ui/components/Toolbar/ToolbarButton";
import { termsEditorInspector } from "../../../../../terms/inspector";
import { RESOURCE_TYPES } from "@adapptio/lib-core";
import {
	FieldGroup,
	FieldText,
	Icon,
	ICON_SIZE,
	InputAdornment,
	InputButton,
	useClipboard
} from "@adapptio/lib-editor-ui";
import { useCommand, useResourceInfo, useViewInfo } from "../../../../../ui/hooks";

export interface IAssetInfoProps {
	className?: string;
	view: AssetEdit;
}

export const AssetInfo: React.FC<IAssetInfoProps> = ({ className, view }) => {
	const baseClass = BEM("edt-inspect-panel");
	baseClass.add("edt-base");

	const t = useTranslate();
	const [ deleteResource ] = useCommand("resource.delete");
	const { copyToClipboard, wasCopiedRecently } = useClipboard();

	const viewInfo = useViewInfo(view);
	const resourceInfo = useResourceInfo(view);

	const { resource } = resourceInfo;

	const assetUrl = resource?.resourceType === RESOURCE_TYPES.ASSET ? resource?.metaData?.assetUrl : "";
	const assetSrc = "${_app.envs.apiUrlProtocol}://${_app.envs.apiUrl}" + assetUrl;

	const handleDelete = useCallback(() => {
		deleteResource(resource?.id);
	}, [ resourceInfo ]);

	const copyUrlToClipboard = useCallback(() => {
		if (resource?.resourceType !== RESOURCE_TYPES.ASSET) {
			return;
		}

		copyToClipboard(assetSrc);
	}, [ copyToClipboard, resource, assetSrc ]);

	if (resource?.resourceType !== RESOURCE_TYPES.ASSET) {
		return null;
	}

	if (className) {
		baseClass.add(className);
	}

	baseClass.add("edt-panel");

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("header").toClassName()}>
				<div className={baseClass.element("header-info").toClassName()}>
					<span className={baseClass.element("header-info-icon").toClassName()}>
						<Icon name={viewInfo.icon} size={ICON_SIZE.SMALL} />
					</span>
					<span className={baseClass.element("header-info-name").toClassName()}>
						{resource?.metaData?.assetMimetype}
					</span>
					<ToolbarButton
						icon="mdi/delete"
						tooltip={t("editor", termsEditorInspector.component.delete)}
						onClick={handleDelete}
					/>
				</div>

				<FieldGroup grow className={baseClass.element("header-row").toClassName()}>
					<FieldText
						value={resource?.descriptor?.name}
						grow
						disabled
						startAdornment={
							<InputAdornment center square>
								<span className={baseClass.element("label").toClassName()}>Name</span>
							</InputAdornment>
						}
					/>
				</FieldGroup>

				<FieldGroup grow className={baseClass.element("header-row").toClassName()}>
					<FieldText
						value={String(resource?.metaData?.assetSize)}
						grow
						disabled
						startAdornment={
							<InputAdornment center square>
								<span className={baseClass.element("label").toClassName()}>Size</span>
							</InputAdornment>
						}
						endAdornment={
							<InputAdornment center square>
								<span className={baseClass.element("label").toClassName()}>bytes</span>
							</InputAdornment>
						}
					/>
				</FieldGroup>

				<FieldGroup grow className={baseClass.element("header-row").toClassName()}>
					<FieldText
						value={resource?.metaData?.assetUrl ? assetSrc : ""}
						grow
						disabled
						startAdornment={
							<InputAdornment center square>
								<span className={baseClass.element("label").toClassName()}>URL</span>
							</InputAdornment>
						}
						endAdornment={
							<InputButton
								className={baseClass.element("editor-clear").toClassName()}
								onClick={copyUrlToClipboard}
								tabIndex={-1}
								disabled={wasCopiedRecently}
								title="Copy url"
							>
								<Icon name={wasCopiedRecently ? "mdi/check" : "mdi/content-copy"} />
							</InputButton>
						}
					/>
				</FieldGroup>
			</div>
		</div>
	);
};
